import React, { useState } from 'react'

import { CheckIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/solid'
import { ExclamationCircleIcon } from '@heroicons/react/24/outline'
import { XMarkIcon } from '@heroicons/react/20/solid'

export const alertTypes = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
} as const

type AlertType = typeof alertTypes[keyof typeof alertTypes]

interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  type?: AlertType
  message?: string
  children?: React.ReactNode
  /**
   * Passing a stable `id` opts the alert into a dismiss (X) affordance. The
   * dismissal persists in localStorage keyed by this id, so once a user closes a
   * given panel it stays closed for them across reloads. Without an `id` the
   * alert is not dismissible and behaves exactly as before.
   */
  id?: string
}

const alertStyles = {
  [alertTypes.info]: { bg: 'bg-botticelli-light', text: 'text-botticelli-dark' },
  [alertTypes.success]: { bg: 'bg-emerald-light', text: 'text-emerald-dark' },
  [alertTypes.warning]: { bg: 'bg-anzac-light', text: 'text-anzac-dark' },
  [alertTypes.danger]: { bg: 'bg-cerise-red-light', text: 'text-cerise-red-dark' },
} as const

const DISMISS_KEY_PREFIX = 'climate:alert-dismissed:'

// localStorage can be absent (SSR) or throw (private mode / disabled cookies).
// Reads default to "not dismissed" and writes fail silently so the alert never
// crashes the page over a storage hiccup.
const readDismissed = (id?: string): boolean => {
  if (!id || typeof window === 'undefined') return false
  try {
    return window.localStorage?.getItem(`${DISMISS_KEY_PREFIX}${id}`) === '1'
  } catch {
    return false
  }
}

const persistDismissed = (id?: string): void => {
  if (!id || typeof window === 'undefined') return
  try {
    window.localStorage?.setItem(`${DISMISS_KEY_PREFIX}${id}`, '1')
  } catch {
    /* storage unavailable: dismissal still applies for this session */
  }
}

const Alert = ({ type = alertTypes.info, message, children, id, ...otherAttrs }: AlertProps) => {
  const dismissible = Boolean(id)
  const [dismissed, setDismissed] = useState(() => readDismissed(id))

  if (dismissed) return null

  const handleDismiss = () => {
    setDismissed(true)
    persistDismissed(id)
  }

  const Icon = () => ({
    [alertTypes.info]: <InformationCircleIcon className="h-7 w-7 text-polo-blue-dark" aria-hidden="true"/>,
    [alertTypes.success]: <CheckIcon className="h-7 w-7 text-emerald" aria-hidden="true"/>,
    [alertTypes.warning]: <ExclamationCircleIcon className="h-7 w-7 text-anzac" aria-hidden="true"/>,
    [alertTypes.danger]: <ExclamationTriangleIcon className="h-7 w-7 text-cerise-red" aria-hidden="true"/>,
  }[type])

  const styles = alertStyles[type]

  return (
    <div id={id} className={`${styles.bg} border sm:rounded-md p-4 text-sm`} {...otherAttrs}>
      <div className="flex items-center">
        <div className="shrink-0">
          <Icon/>
        </div>
        <div className="ml-3 flex-1">
          {message && (
            <p className={styles.text}>
              {message}
            </p>)
          }
          {children}
        </div>
        {dismissible && (
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss"
            className={`ml-3 shrink-0 inline-flex rounded-md p-1 ${styles.text} hover:opacity-70 focus:outline-hidden focus:ring-2 focus:ring-perfect-shine cursor-pointer`}
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true"/>
          </button>
        )}
      </div>
    </div>
  )
}

export default Alert
